<template>
  <div ref="map_view" class="map-container w-100 vw-100"></div>

  <div class="bg-white p-2 rounded-5 bg-opacity-75"
       style="position: absolute; z-index: 1000; top: 50%; left: 50%" v-if="loadingLayers === true">
    <div class="d-flex justify-content-center">
      <div class="spinner-grow spinner-grow-sm text-purple" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>
  </div>

  <Legend prop-id="legend_content_visualization"
          :prop-unit="controlLegendUnit"
          :prop-legend-color-artifact="controlLegendColorArtifact"
          v-if="controlLegend === true"
  />

  <LegendColor
      v-if="controlMapOptions.show"
      :prop-rivers-show="controlMapOptions.water_resources"
      :prop-glacier-show="controlMapOptions.glaciers"
      :prop-lakes-show="controlMapOptions.lakes"
      :prop-agriculture-area-show="controlMapOptions.agriculture_areas"
      :prop-administrative-units-show="controlMapOptions.municipalities"
      :prop-anthropic-works-show="controlMapOptions.anthropic_works === true"
      :prop-lakes-natural="controlMapOptions.counters.lakes_natural"
      :prop-glaciers="controlMapOptions.counters.glaciers"
      :prop-lakes="controlMapOptions.counters.lakes"
      :prop-derivations="controlMapOptions.counters.derivations"
      :prop-derivation-points="controlMapOptions.counters.derivation_points"
      :prop-stations="controlMapOptions.counters.stations"
  />

  <LayerInfo
      prop-id="info_box_content_visualization"
      :prop-title1="layerTitle1"
      :prop-title2="layerTitle2"
      :prop-title3="null"
      :prop-title3-object="layerTitle3Objects"

      :prop-layer-areas-selectable="layerAreaSelection"
      :prop-layer-hover-feature-id="controlInfoLayerHoverFeatureId"
      :prop-layer-variable-configuration="layerVariableConfiguration"
      :prop-toggle="layerAreaSelectionCountUpdate"

      :prop-area-multiple-selection="layerAreaMultipleSelection"
      :prop-show-municipalities="controlMapOptions.municipalities === true"
      v-if="controlInfoBox === true"
  />

  <TimeSeriesControl
      v-if="controlTimeSeries.show === true"
      prop-id="time-series-control-visualization"
      @on-data-click="controlActionsTimeSeriesButtonClick"
      @on-clear-selection-click="controlActionsTimeSeriesButtonClearSelectionClick"
      @on-select-all-click="controlActionsTimeSeriesButtonSelectAllClick"
      :prop-locale="propLocale"
      :prop-loading-data="loadingData"
      :prop-tooltip-trigger-show="controlTimeSeries.triggers.show"
      :prop-buttons-multiple-select="controlTimeSeries.multiple"
      :prop-button-data-disabled="timeSeriesButtonDataDisabled"
      :prop-button-select-all-disabled="timeSeriesButtonSelectAllDisabled"
      :prop-button-clear-selection-disabled="timeSeriesButtonClearSelectionDisabled"
  />

  <MapOptions prop-id="mapOptionsSimulation"
              :prop-options="mapOptions"
              :prop-show-tooltip="controlMapOptions.tooltip"
              :prop-disabled="loadingData === true || propParentLoading === true || loadingLayers === true"
              @on-switch-change="mapLayerOptionsOnSwitch"/>

  <!-- Tooltip content for water infrastructure items on mouse over -->
  <div class="d-none" ref="tooltipWater">
    <div class="row">
      <div class="col text-center" style="font-size: 0.9rem">
        <div class="fw-semibold">{{ controlTooltipWater.name }}</div>
        <div class="badge rounded-pill me-1" :class="anthropicWorkBadgeColorClass(controlTooltipWater.object_type)">
          {{ controlTooltipWater.object_type_name }}
        </div>
      </div>
    </div>
  </div>

</template>
<script>
import L from 'leaflet';
import {toRaw} from 'vue';
import {Locale} from "vue-i18n";
import 'leaflet/dist/leaflet.css';
import constants from "@/libs/Constants";
import Legend from "@/components/Legend.vue";
import {VisualizationGeoJsonDataAbstract} from '@/libs/VisualizationData';
import LayerInfo from "@/views/Visualization/MapContents/LayerInfo.vue";
import {
  anthropicWorkBadgeColorClass,
  getPercentileI18NLabel,
  getSeasonI18NLabel,
  getSeasonTooltipI18NLabel,
  getVisualizationComponentI18NLabel,
  getWaterInfrastructureTypeI18NLabel
} from '@/libs/Helpers';
import {
  LayerAreasSelectable,
  layerMapOSMLoad,
  layerSimulationLoad,
  mapLayerWaterStructureStyle,
  layerLoadWrapper,
  layerDerivationCreateDirectionArrows
} from "@/libs/Layers";
import MapOptions from "@/components/MapOptions.vue";
import TimeSeriesControl from "@/components/TimeSeriesControl.vue";
import LegendColor from '@/components/LegendColor.vue';
import ColorLegend from "@/components/LegendColor.vue";

export default {
  name: 'MapView',
  emits: ['onTimeSeriesButtonClickReady', 'onTimeSeriesDataLoading'],
  components: {ColorLegend, LegendColor, MapOptions, TimeSeriesControl, Legend, LayerInfo},
  props: {
    propLocale: {type: Locale},
    propPlotLoading: Boolean,
    propFormData: VisualizationGeoJsonDataAbstract || null,

    //Loading from parent component
    propParentLoading: Boolean,
  },
  watch: {
    propFormData(newFormData, oldFormData) {
      if (newFormData) {
        this.controlMapOptions.tooltip = false;

        // Handle the new form data (e.g., update LayerInfo with selectedDate)
        this.layerVariableLoadGeoJSON(newFormData.geoJson);

        this.controlLayerInfoUpdate();

        this.lastDataUUID = null;
      }
    },
    propPlotLoading(newValue, oldValue) {
      if (newValue === false)
        this.loadingData = false;
    },
    propLocale(newData, oldData) {
      this.controlLayerInfoUpdate();
    }
  },
  data() {
    /*************************
     * Action button objects *
     *************************/
    /* Color configuration */
    let colors = {
      black: '#000000',
      green: '#424d42',
      lightblue: '#2D91CB',
      gray: 'gray'
    }

    return {
      colors: colors,

      map: null,
      layersMap: {},
      layerAreaSelection: null, // To keep track of selected layers and behaviour of map
      layerAreaSelectionCount: 0,
      layerAreaSelectionCountUpdate: false, //count of selected areas
      layerVariableConfiguration: null, //Configuration of selected variable

      //Legend variables
      controlLegend: false,

      //Titles
      layerTitle1: '',
      layerTitle2: '',
      layerTitle3Objects: null,

      //Info box
      controlInfoBox: false,
      controlInfoLayerInfoToggle: false,
      controlInfoLayerHoverFeatureId: null,

      //Action buttons
      controlScale: null,
      controlZoom: null,
      controlActions: null,
      loadingData: false,
      loadingLayers: true,

      controlTimeSeries: {
        show: false,
        multiple: true,
        triggers: {
          show: false,
          hide: false
        }
      },

      controlMapOptions: {
        show: false,
        tooltip: false,
        grayscale: false,
        water_resources: true,
        municipalities: false,
        anthropic_works: true,
        glaciers: true,
        lakes: true,
        agriculture_areas: false,
        counters: {
          lakes_natural: 0,
          glaciers: 0,
          lakes: 0,
          derivations: 0,
          stations: 0,
          derivation_points: 0
        }
      },

      controlTooltipWater: {
        name: null,
        object_type: null,
        object_type_name: null
      },

      //UUID for last search combination
      lastDataUUID: null,
    }
  },
  mounted() {
    this.mapInitialize();
  },
  computed: {
    layersCountSelected() {
      return this.layerAreaSelectionCount;
    },
    layerCountTotalFeatures() {
      return this.layerAreaSelection !== null ? this.layerAreaSelection.layersCount : 0;
    },
    controlLegendUnit() {
      return this.propFormData !== null ? this.propFormData.variableConfiguration.unit : '';
    },
    controlLegendColorArtifact() {
      return this.layerAreaSelection !== null ? this.layerAreaSelection.legendColorArtifactObject : null;
    },
    timeSeriesButtonDataDisabled() {
      return this.layersCountSelected === 0 || this.propParentLoading === true || this.loadingData === true;
    },
    timeSeriesButtonSelectAllDisabled() {
      return (this.layerCountTotalFeatures > 0 && this.layersCountSelected === this.layerCountTotalFeatures) ||
          this.propParentLoading === true ||
          this.loadingData === true;
    },
    timeSeriesButtonClearSelectionDisabled() {
      return this.layersCountSelected === 0 || this.propParentLoading === true || this.loadingData === true;
    },
    layerAreaMultipleSelection() {
      let allow = true;
      if (this.propFormData !== null) {
        let layerData = this.propFormData.variableConfiguration;
        if (layerData !== undefined && layerData !== null)
          allow = layerData.area_multiple_selection;
      }
      return allow;
    },
    mapOptions() {
      return [
        {
          id: constants.MAP_OPTIONS.MAP_GRAYSCALE,
          selected: false,
          label: this.$t('SimulationInputs.GrayscaleMap')
        },
        {
          id: constants.MAP_OPTIONS.WATER_RESOURCES,
          selected: true,
          label: this.$t('SimulationInputs.WaterResources')
        },
        {
          id: constants.MAP_OPTIONS.LAKES,
          selected: true,
          label: this.$t('SimulationInputs.Lakes')
        },
        {
          id: constants.MAP_OPTIONS.GLACIER,
          selected: true,
          label: this.$t('SimulationInputs.Glaciers')
        },
        {
          id: constants.MAP_OPTIONS.MUNICIPALITIES,
          selected: false,
          label: this.$t('SimulationInputs.Municipalities')
        },
        {
          id: constants.MAP_OPTIONS.AGRICULTURE_AREA,
          selected: false,
          label: this.$t('SimulationInputs.AgricultureAreas')
        },
        {
          id: constants.MAP_OPTIONS.ANTHROPIC_WORKS,
          selected: true,
          label: this.$t('SimulationInputs.AnthropicWorks')
        }
      ]
    }
  },
  methods: {
    anthropicWorkBadgeColorClass,
    mapLayerReloadByKeys(layerKeys) {
      layerKeys.forEach(layerKey => {
        if (this.layersMap.hasOwnProperty(layerKey) === true) {
          let layerObject = this.layersMap[layerKey];

          if (this.map.hasLayer(layerObject))
            this.map.removeLayer(layerObject);
          layerObject.addTo(toRaw(this.map));
        }
      })
    },
    mapLayerClearByKey(layerKey, deleteLayer = false) {
      if (this.layersMap.hasOwnProperty(layerKey) === true) {
        let layerObject = this.layersMap[layerKey];

        if (this.map.hasLayer(layerObject)) {
          this.map.removeLayer(layerObject);

          if (deleteLayer === true)
            delete this.layersMap[layerKey];
        }
      }
    },
    mapLayerVariableClear() {
      this.mapLayerClearByKey(constants.LAYERS.VARIABLE, true);
    },
    mapLayerReloadVisible() {
      this.mapLayerOSMLoad();

      //Area
      this.mapLayerReloadByKeys([constants.LAYERS.AREA]);

      //Municipalities
      if (this.controlMapOptions.municipalities === true)
        this.mapLayerReloadByKeys([constants.LAYERS.MUNICIPALITIES]);
      else
        this.mapLayerClearByKey(constants.LAYERS.MUNICIPALITIES);

      //Rivers
      if (this.controlMapOptions.water_resources === true)
        this.mapLayerReloadByKeys([constants.LAYERS.RIVER]);
      else
        this.mapLayerClearByKey(constants.LAYERS.RIVER);

      //Variable
      this.mapLayerReloadByKeys([constants.LAYERS.VARIABLE]);

      //Background layers
      let mapOptionsKeys = {
        'glaciers': constants.LAYERS.GLACIER,
        'lakes': constants.LAYERS.LAKES,
        'agriculture_areas': constants.LAYERS.AGRICULTURE_AREA
      };

      Object.keys(mapOptionsKeys).forEach(layerKey => {
        let layerId = mapOptionsKeys[layerKey];

        if (this.controlMapOptions[layerKey] === true)
          this.mapLayerReloadByKeys([layerId]);
        else
          this.mapLayerClearByKey(layerId);
      });

      //Water infrastructures
      let layerKeys = [
        constants.LAYERS.DERIVATION,
        constants.LAYERS.DERIVATION_POINTS,
        constants.LAYERS.DERIVATION_DIRECTION,
        constants.LAYERS.DERIVATION_DIRECTION_ARROW,
        constants.LAYERS.DAM_LAKE,
        constants.LAYERS.PLANT,
      ];

      if (this.controlMapOptions.anthropic_works === true)
        this.mapLayerReloadByKeys(layerKeys);
      else {
        layerKeys.forEach(layerKey => {
          this.mapLayerClearByKey(layerKey);
        })
      }

    },
    mapLayerOptionsOnSwitch(optionId, value) {
      let optionMap = {
        [constants.MAP_OPTIONS.MAP_GRAYSCALE]: 'grayscale',
        [constants.MAP_OPTIONS.WATER_RESOURCES]: 'water_resources',
        [constants.MAP_OPTIONS.MUNICIPALITIES]: 'municipalities',
        [constants.MAP_OPTIONS.ANTHROPIC_WORKS]: 'anthropic_works',
        [constants.MAP_OPTIONS.LAKES]: 'lakes',
        [constants.MAP_OPTIONS.GLACIER]: 'glaciers',
        [constants.MAP_OPTIONS.AGRICULTURE_AREA]: 'agriculture_areas'
      };

      if (optionMap.hasOwnProperty(optionId)) {
        let optionMapped = optionMap[optionId];
        this.controlMapOptions[optionMapped] = value;
        this.mapLayerReloadVisible();
      }
    },
    mapOSMLayer(grayscale = false) {
      return layerMapOSMLoad(9, 15, grayscale);
    },
    mapLayerOSMLoad() {
      this.layersMap[constants.LAYERS.MAP] = this.mapOSMLayer(this.controlMapOptions.grayscale);
      this.mapLayerReloadByKeys([constants.LAYERS.MAP]);
    },
    mapLayerHoverableOnEachFeature(feature, layer, featureNameKey, featureType) {
      layer.on('mouseover', () => {
        this.controlTooltipWater.name = feature.properties[featureNameKey];
        this.controlTooltipWater.object_type = featureType;
        this.controlTooltipWater.object_type_name = this.$t(getWaterInfrastructureTypeI18NLabel(featureType));

        this.$nextTick(() => {
          layer.bindTooltip(this.$refs.tooltipWater.innerHTML).openTooltip();
        });
      });
    },
    mapLayerGlacierOnEachFeature(feature, layer) {
      this.mapLayerHoverableOnEachFeature(feature, layer, 'name', constants.LAYERS.GLACIER);
    },
    mapLayerLakeOnEachFeature(feature, layer) {
      this.mapLayerHoverableOnEachFeature(feature, layer, 'name', constants.LAYERS.LAKES);
    },
    mapLayerDerivationPointOnEachFeature(feature, layer) {
      this.mapLayerHoverableOnEachFeature(feature, layer, 'name', constants.LAYERS.DERIVATION_POINTS);
    },
    mapLayerDamOnEachFeature(feature, layer) {
      this.mapLayerHoverableOnEachFeature(feature, layer, 'Nome', constants.LAYERS.DAM_LAKE);
    },
    mapLayerDerivationOnEachFeature(feature, layer) {
      this.mapLayerHoverableOnEachFeature(feature, layer, 'Nome', constants.LAYERS.DERIVATION);
    },
    mapLayerHydroelectricPlantOnEachFeature(feature, layer) {
      this.mapLayerHoverableOnEachFeature(feature, layer, 'Nome', constants.LAYERS.PLANT);
    },
    async mapInitialize() {
      this.map = L.map(this.$refs.map_view, {zoomControl: false}).setView([46.052926, 10.798974], 10);

      //Load OSM layer
      this.mapLayerOSMLoad();

      this.map.attributionControl.setPosition('bottomleft');

      let hoverableMapFunction = {
        [constants.LAYERS.LAKES]: this.mapLayerLakeOnEachFeature,
        [constants.LAYERS.GLACIER]: this.mapLayerGlacierOnEachFeature,
        [constants.LAYERS.DERIVATION]: this.mapLayerDerivationOnEachFeature,
        [constants.LAYERS.DAM_LAKE]: this.mapLayerDamOnEachFeature,
        [constants.LAYERS.PLANT]: this.mapLayerHydroelectricPlantOnEachFeature,
        [constants.LAYERS.DERIVATION_POINTS]: this.mapLayerDerivationPointOnEachFeature
      }

      /* ------------------------------
       * Load GeoJSON layers to the map
       * ------------------------------ */
      let layerPromises = [];

      let layerKeys = [
        constants.LAYERS.AREA,
        constants.LAYERS.RIVER,
        constants.LAYERS.MUNICIPALITIES,
        constants.LAYERS.GLACIER,
        constants.LAYERS.LAKES,
        constants.LAYERS.AGRICULTURE_AREA,
        constants.LAYERS.DERIVATION_DIRECTION,
        constants.LAYERS.DERIVATION_POINTS
      ];

      let layerWaterKeys = [
        constants.LAYERS.DERIVATION,
        constants.LAYERS.DAM_LAKE,
        constants.LAYERS.PLANT
      ];

      layerKeys.forEach(layer => {
        if (hoverableMapFunction.hasOwnProperty(layer))
          layerPromises.push(layerLoadWrapper(layer, true, hoverableMapFunction[layer]));
        else
          layerPromises.push(layerLoadWrapper(layer, true));
      })

      layerWaterKeys.forEach(layer => {
        layerPromises.push(layerSimulationLoad(layer));
      })

      let layerCounterMap = {
        [constants.LAYERS.DERIVATION]: 'derivations',
        [constants.LAYERS.DAM_LAKE]: 'lakes',
        [constants.LAYERS.PLANT]: 'stations',
        [constants.LAYERS.LAKES]: 'lakes_natural',
        [constants.LAYERS.GLACIER]: 'glaciers',
        [constants.LAYERS.DERIVATION_POINTS]: 'derivation_points'
      };

      Promise.allSettled(layerPromises).then((values) => {
            let dataIndex = 0;

            //Fixed layers: keep them in list
            layerKeys.forEach(layerKey => {
              let layerData = values[dataIndex];
              if (layerData.status === "fulfilled" && layerData.value !== null) {
                let layerResult = layerData.value;

                let layerLeaflet = layerResult.object;
                this.layersMap[layerKey] = layerLeaflet;

                // Add arrowheads to the line
                if (layerKey === constants.LAYERS.DERIVATION_DIRECTION)
                  this.layersMap[constants.LAYERS.DERIVATION_DIRECTION_ARROW] = layerDerivationCreateDirectionArrows(layerLeaflet);

                if (layerCounterMap.hasOwnProperty(layerKey)) {
                  let counterKey = layerCounterMap[layerKey];
                  this.controlMapOptions.counters[counterKey] = layerResult.count;
                }

              }
              dataIndex += 1;
            })

            dataIndex = layerKeys.length;

            //Water infrastructure layers: set behaviour and keep them in list
            layerWaterKeys.forEach(layerKey => {
              let layerData = values[dataIndex];
              if (layerData.status === "fulfilled" && layerData.value !== null) {
                let layerObjectStyleConfig = mapLayerWaterStructureStyle(layerKey);

                let layerOptions = {
                  onEachFeature: hoverableMapFunction[layerKey]
                };

                if (layerObjectStyleConfig.styleFunction !== null)
                  layerOptions.style = layerObjectStyleConfig.styleFunction;

                if (layerObjectStyleConfig.stylePointFunction !== null)
                  layerOptions.pointToLayer = (feature, latLong) => {
                    return layerObjectStyleConfig.stylePointFunction(latLong)
                  };

                //Add layer
                let layerDataContent = layerData.value;
                this.layersMap[layerKey] = L.geoJSON(layerDataContent, layerOptions);

                let counterKey = layerCounterMap[layerKey];
                this.controlMapOptions.counters[counterKey] = layerDataContent.features.length;
              }
              dataIndex += 1;
            });

            /**
             * Initial layers to show
             */
            this.mapLayerReloadByKeys([
              constants.LAYERS.AREA,
              constants.LAYERS.RIVER,
              constants.LAYERS.GLACIER,
              constants.LAYERS.LAKES,

              //Water infrastructures
              constants.LAYERS.DERIVATION,
              constants.LAYERS.DERIVATION_POINTS,
              constants.LAYERS.DERIVATION_DIRECTION,
              constants.LAYERS.DERIVATION_DIRECTION_ARROW,
              constants.LAYERS.DAM_LAKE,
              constants.LAYERS.PLANT
            ]);
          }
      ).finally(() => {
        this.loadingLayers = false;
        this.controlMapOptions.show = true;
      });

      this.controlButtonsFixedAddOrReplace();
    },
    /**********************
     * Map with variables *
     **********************/
    layerVariableOnAreaSelectedLayersChange(count) {
      this.layerAreaSelectionCount = count;

      //Force LayerInfo component to refresh
      this.layerAreaSelectionCountUpdate = !this.layerAreaSelectionCountUpdate;
    }
    ,
    layerVariableOnAreaMouseOver(layerFeatureId) {
      this.controlInfoLayerHoverFeatureId = layerFeatureId;
    }
    ,
    layerVariableOnAreaMouseOut() {
      this.controlInfoLayerHoverFeatureId = null;
    }
    ,
    layerVariableLoadGeoJSON(data) {
      if (this.map && data) {

        //Clear previous layer data
        this.mapLayerVariableClear();

        //Clear selection
        this.controlActionsTimeSeriesButtonClearSelectionClick();

        //Update layer data
        let variableConfiguration = this.propFormData.variableConfiguration;

        let layerSelectable = new LayerAreasSelectable(
            data,
            variableConfiguration.unit,
            variableConfiguration.decimals,
            variableConfiguration.palette,
            this.propFormData.variableConfiguration.area_multiple_selection,
            null,
            this.layerVariableOnAreaMouseOver,
            this.layerVariableOnAreaMouseOut,
            this.layerVariableOnAreaSelectedLayersChange
        );

        this.layerAreaSelection = layerSelectable;
        this.layerVariableConfiguration = variableConfiguration;

        this.layerAreaSelectionCount = 0;
        this.layerAreaSelectionCountUpdate = false;

        this.layersMap[constants.LAYERS.VARIABLE] = layerSelectable.layerLeaflet;

        /*******************
         Add controls to map
         *******************/
        // Buttons for different actions
        this.controlActionsAddOrReplace();

        //Info box
        this.controlLayerInfoAddOrReplace();

        //Check if palette of colors for legend is set, otherwise hide legend box
        if (this.controlLegendColorScale !== null)
          this.controlLegendShow();
        else
          this.controlLegendRemove();

        this.mapLayerReloadVisible();
      }
    }
    ,

    /************
     * Controls *
     ************/
    controlZoomButtonAddOrReplace() {
      if (this.controlZoom) {
        this.map.removeControl(this.controlZoom);
        this.controlZoom = null;
      }
      this.controlZoom = L.control.zoom({
        position: 'bottomleft'
      });
      this.controlZoom.addTo(toRaw(this.map));
    }
    ,
    controlScaleAddOrReplace() {
      if (this.controlScale) {
        this.map.removeControl(this.controlScale);
        this.controlScale = null;
      }
      this.controlScale = L.control.scale();
      this.controlScale.addTo(toRaw(this.map));
    }
    ,
    controlButtonsFixedAddOrReplace() {
      this.controlScaleAddOrReplace();
      this.controlZoomButtonAddOrReplace();
    }
    ,

    controlLayerInfoAddOrReplace() {
      this.controlInfoLayerHoverFeatureId = null;
      this.controlInfoBox = true;
    }
    ,
    controlLayerInfoUpdate() {
      //Clear objects
      this.layerTitle1 = null;
      this.layerTitle2 = null;
      this.layerTitle3Objects = {};
      let percentileLabel = '';

      let formData = this.propFormData;

      if (formData !== null) {
        let labelTitle1 = getVisualizationComponentI18NLabel(formData.component);
        this.layerTitle1 = this.$t(labelTitle1);

        //Translate variable name from configuration
        let variableConfiguration = formData.variableConfiguration;
        this.layerTitle2 = variableConfiguration.getNameI18N(this.$i18n.locale);

        switch (formData.component) {
          case constants.COMPONENTS.MONITORING:
            this.layerTitle3Objects[this.$t('InputSearchFormLabelTitle.Month')] = formData.monthYearI18N(this.$i18n);
            break;
          case constants.COMPONENTS.FORECAST:
            percentileLabel = getPercentileI18NLabel(formData.percentile);

            if (formData.visualizationMode === constants.VISUALIZATION_MODE.ADVANCED)
              this.layerTitle3Objects[this.$t('InputSearchFormLabelTitle.Percentile')] = this.$t(percentileLabel);

            this.layerTitle3Objects[this.$t('InputSearchFormLabelTitle.CreationMonth')] = formData.creationMonthYearI18N(this.$i18n);
            this.layerTitle3Objects[this.$t('InputSearchFormLabelTitle.ForecastMonth')] = formData.forecastMonthYearI18N(this.$i18n);
            break;

          case constants.COMPONENTS.PROJECTION:
            let seasonName = this.$t(getSeasonI18NLabel(formData.season));
            let seasonMonths = this.$t(getSeasonTooltipI18NLabel(formData.season));
            this.layerTitle3Objects[this.$t('InputSearchFormLabelTitle.Year')] = formData.year;
            this.layerTitle3Objects[this.$t('InputSearchFormLabelTitle.Season')] = `${seasonName} (${seasonMonths})`;
            this.layerTitle3Objects[this.$t('InputSearchFormLabelTitle.climateChangeScenario')] = formData.climateScenarioLabelI18N(this.$i18n);
            break;
        }
      }
    }
    ,
    controlLegendRemove() {
      this.controlLegend = false;
    }
    ,
    controlLegendShow() {
      this.controlLegend = true;
    }
    ,
    controlActionsAddOrReplace() {
      this.controlTimeSeries.multiple = this.layerAreaMultipleSelection;
      this.controlTimeSeries.show = true;
      this.controlTimeSeries.triggers.show = !this.controlTimeSeries.triggers.show;
    }
    ,
    controlActionsTimeSeriesButtonClick() {
      this.loadingData = true;
      this.$emit('onTimeSeriesDataLoading');

      if (this.propFormData !== null && this.layerAreaSelection !== null && this.layersCountSelected > 0) {
        this.$emit(
            'onTimeSeriesButtonClickReady',
            this.propFormData.component,
            this.layerAreaSelection.layersSelectedIdsSorted,
            this.layerAreaSelection.layerSelectedNameList,
            this.layerAreaSelection.layersCount
        );
      }
    }
    ,
    controlActionsTimeSeriesButtonSelectAllClick() {
      if (this.layerAreaSelection !== null && this.layerCountTotalFeatures > 0)
        this.layerAreaSelection.layersSelectAll();
    }
    ,
    controlActionsTimeSeriesButtonClearSelectionClick() {
      if (this.layerAreaSelection !== null)
        this.layerAreaSelection.layersClearAll();
    }
    ,
  }
}

</script>